import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 5  -  Particle Accelerators
// ============================================

/** 5.2 – RF Cavity Acceleration */
export const RFCavity = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Radio-Frequency (RF) Cavity Acceleration', W / 2, 24);

        const cy = 130;
        // Beam pipe
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(30, cy - 12, 500, 24);
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(30, cy - 12); ctx.lineTo(530, cy - 12); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(30, cy + 12); ctx.lineTo(530, cy + 12); ctx.stroke();

        // RF cavity (resonant structure)
        const cavities = [
            { x: 140, phase: '+' },
            { x: 260, phase: '-' },
            { x: 380, phase: '+' },
        ];
        cavities.forEach(cav => {
            // Cavity walls
            ctx.fillStyle = '#FCD34D'; ctx.fillRect(cav.x - 25, cy - 40, 8, 28);
            ctx.fillRect(cav.x + 17, cy - 40, 8, 28);
            ctx.fillRect(cav.x - 25, cy + 12, 8, 28);
            ctx.fillRect(cav.x + 17, cy + 12, 8, 28);

            // Gap
            ctx.fillStyle = cav.phase === '+' ? '#DCFCE7' : '#FEE2E2';
            ctx.fillRect(cav.x - 17, cy - 12, 34, 24);

            // E-field arrows in gap
            ctx.strokeStyle = cav.phase === '+' ? '#22C55E' : '#EF4444';
            ctx.lineWidth = 2;
            const dir = cav.phase === '+' ? 1 : -1;
            ctx.beginPath(); ctx.moveTo(cav.x - 10 * dir, cy); ctx.lineTo(cav.x + 10 * dir, cy); ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cav.x + 10 * dir, cy);
            ctx.lineTo(cav.x + 10 * dir - 5 * dir, cy - 4);
            ctx.moveTo(cav.x + 10 * dir, cy);
            ctx.lineTo(cav.x + 10 * dir - 5 * dir, cy + 4);
            ctx.stroke();

            // Phase label
            ctx.fillStyle = cav.phase === '+' ? '#16A34A' : '#DC2626';
            ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(cav.phase === '+' ? '+E' : '-E', cav.x, cy - 48);
        });

        // Particle beam
        ctx.fillStyle = '#3B82F6';
        [60, 140, 260, 380, 490].forEach(x => {
            ctx.beginPath(); ctx.arc(x, cy, 5, 0, Math.PI * 2); ctx.fill();
        });

        // Velocity arrows (increasing)
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5;
        const arrows = [{ x: 60, len: 15 }, { x: 200, len: 25 }, { x: 320, len: 35 }, { x: 440, len: 45 }];
        arrows.forEach(a => {
            ctx.beginPath(); ctx.moveTo(a.x, cy + 30); ctx.lineTo(a.x + a.len, cy + 30); ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(a.x + a.len, cy + 30);
            ctx.lineTo(a.x + a.len - 5, cy + 26);
            ctx.moveTo(a.x + a.len, cy + 30);
            ctx.lineTo(a.x + a.len - 5, cy + 34);
            ctx.stroke();
        });

        // Labels
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Particle gains energy at each gap', W / 2, cy + 55);

        // RF timing diagram (bottom)
        const ty = 210;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
        ctx.fillText('RF Oscillation (timing)', W / 2, ty);

        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2;
        ctx.beginPath();
        for (let px = 0; px < 400; px++) {
            const x = 80 + px;
            const y = ty + 30 + Math.sin(px * 0.04) * 20;
            if (px === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();

        ctx.fillStyle = '#F59E0B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Particles timed to arrive when field pushes forward', W / 2, ty + 62);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('RF cavities are the heart of every modern accelerator', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.3 – Magnetic Bending and Steering */
export const MagneticBending = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 290;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Magnetic Steering: The Lorentz Force', W / 2, 24);

        // B-field region (shaded)
        ctx.fillStyle = '#EDE9FE'; ctx.beginPath(); ctx.roundRect(140, 45, 240, 190, 12); ctx.fill();
        ctx.strokeStyle = '#C4B5FD'; ctx.lineWidth = 1.5; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.roundRect(140, 45, 240, 190, 12); ctx.stroke();
        ctx.setLineDash([]);

        // B-field dots (out of page)
        ctx.fillStyle = '#8B5CF6'; ctx.font = '14px Inter,sans-serif';
        for (let r = 0; r < 4; r++) {
            for (let col = 0; col < 5; col++) {
                const x = 170 + col * 45;
                const y = 75 + r * 42;
                ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill();
                ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.strokeStyle = '#C4B5FD'; ctx.lineWidth = 1; ctx.stroke();
            }
        }
        ctx.fillStyle = '#8B5CF6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('B field (out of page)', 260, 248);

        // Particle path: straight entry → curved arc → straight exit
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        // Straight entry (left)
        ctx.beginPath(); ctx.moveTo(30, 140); ctx.lineTo(140, 140); ctx.stroke();
        // Arrow on entry
        ctx.fillStyle = '#3B82F6';
        ctx.beginPath(); ctx.moveTo(140, 140); ctx.lineTo(132, 136); ctx.lineTo(132, 144); ctx.closePath(); ctx.fill();

        // Curved path (arc)
        const arcCx = 260, arcCy = 280, arcR = 160;
        ctx.beginPath();
        ctx.arc(arcCx, arcCy, arcR, -Math.PI * 0.76, -Math.PI * 0.35, false);
        ctx.stroke();

        // Straight exit (top)
        const exitX = arcCx + arcR * Math.cos(-Math.PI * 0.35);
        const exitY = arcCy + arcR * Math.sin(-Math.PI * 0.35);
        ctx.beginPath(); ctx.moveTo(exitX, exitY); ctx.lineTo(exitX + 60, exitY - 50); ctx.stroke();

        // Radius arrow
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(arcCx, arcCy - 30); ctx.lineTo(210, 140); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('r', 240, 205);

        // Particle dot
        ctx.beginPath(); ctx.arc(210, 140, 6, 0, Math.PI * 2); ctx.fillStyle = '#3B82F6'; ctx.fill();
        ctx.fillStyle = '#3B82F6'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('p⁺', 220, 136);

        // Formulas (right side)
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('F = qv �- B', 400, 70);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('r = p / (qB)', 400, 95);
        ctx.fillText('Higher B → tighter bend', 400, 120);
        ctx.fillText('Higher p → wider bend', 400, 140);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Dipole magnets bend the beam; quadrupoles focus it', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={290} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.4 – Linear Accelerator (Linac) Design */
export const LinacDesign = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Linear Accelerator (Linac)  -  Drift Tube Design', W / 2, 24);

        const cy = 110;
        // Source
        ctx.fillStyle = '#FCD34D'; ctx.beginPath(); ctx.arc(40, cy, 14, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#92400E'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Ion', 40, cy + 4); ctx.fillText('Source', 40, cy + 30);

        // Drift tubes (increasing length)
        const tubes = [
            { x: 80, w: 40 }, { x: 140, w: 55 }, { x: 220, w: 70 },
            { x: 315, w: 85 }, { x: 425, w: 100 },
        ];
        const gaps = [];
        tubes.forEach((t, i) => {
            // Tube body
            ctx.fillStyle = '#CBD5E1';
            ctx.beginPath(); ctx.roundRect(t.x, cy - 20, t.w, 40, 6); ctx.fill();
            ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5;
            ctx.beginPath(); ctx.roundRect(t.x, cy - 20, t.w, 40, 6); ctx.stroke();

            // Gap between tubes
            if (i > 0) {
                const gapX = tubes[i - 1].x + tubes[i - 1].w;
                const gapW = t.x - gapX;
                gaps.push({ x: gapX, w: gapW });

                // E-field in gap
                ctx.fillStyle = '#DCFCE7'; ctx.fillRect(gapX, cy - 12, gapW, 24);
                ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 1.5;
                const mid = gapX + gapW / 2;
                ctx.beginPath(); ctx.moveTo(gapX + 3, cy); ctx.lineTo(gapX + gapW - 3, cy); ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(gapX + gapW - 3, cy);
                ctx.lineTo(gapX + gapW - 8, cy - 3);
                ctx.moveTo(gapX + gapW - 3, cy);
                ctx.lineTo(gapX + gapW - 8, cy + 3);
                ctx.stroke();
            }

            // Tube label
            if (i < 3) {
                ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
                ctx.fillText('Tube ' + (i + 1), t.x + t.w / 2, cy - 26);
            }
        });

        // Emerging beam
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(525, cy); ctx.lineTo(550, cy); ctx.stroke();
        ctx.fillStyle = '#3B82F6';
        ctx.beginPath(); ctx.moveTo(550, cy); ctx.lineTo(544, cy - 4); ctx.lineTo(544, cy + 4); ctx.closePath(); ctx.fill();

        // Particle beam dots
        ctx.fillStyle = '#3B82F6';
        [65, 130, 205, 300, 412].forEach(x => {
            ctx.beginPath(); ctx.arc(x, cy, 4, 0, Math.PI * 2); ctx.fill();
        });

        // Annotations
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Tubes get longer → particle takes same time in each (it speeds up)', W / 2, cy + 55);

        // RF connection
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(200, cy + 20); ctx.lineTo(200, cy + 70); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('← RF alternates polarity each half-cycle →', W / 2, cy + 85);

        // Key properties
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Key:', 30, 210);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('No bending → no synchrotron radiation loss', 80, 210);
        ctx.fillText('Limited by physical length (SLAC = 3.2 km)', 80, 228);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.5 – Accelerator Evolution Timeline */
export const AcceleratorEvolution = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Evolution of Particle Accelerators', W / 2, 24);

        const events = [
            { year: '1932', name: 'Cockcroft-Walton', energy: '0.7 MeV', desc: 'First nuclear reaction', color: '#3B82F6' },
            { year: '1932', name: 'Cyclotron', energy: '1.2 MeV', desc: 'Lawrence & Livingston', color: '#22C55E' },
            { year: '1952', name: 'Cosmotron', energy: '3.3 GeV', desc: 'First synchrotron', color: '#F59E0B' },
            { year: '1983', name: 'Tevatron', energy: '1 TeV', desc: 'First TeV machine', color: '#EF4444' },
            { year: '2008', name: 'LHC', energy: '13 TeV', desc: 'Largest ever built', color: '#8B5CF6' },
        ];

        // Timeline axis
        const axY = 160, axL = 60, axR = 500;
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(axL, axY); ctx.lineTo(axR, axY); ctx.stroke();

        const gap = (axR - axL) / (events.length - 1);

        events.forEach((ev, i) => {
            const x = axL + i * gap;
            const above = i % 2 === 0;

            // Dot on timeline
            ctx.beginPath(); ctx.arc(x, axY, 7, 0, Math.PI * 2);
            ctx.fillStyle = ev.color; ctx.fill();

            // Connector line
            ctx.strokeStyle = ev.color; ctx.lineWidth = 2;
            const cardY = above ? axY - 100 : axY + 15;
            ctx.beginPath(); ctx.moveTo(x, axY + (above ? -7 : 7)); ctx.lineTo(x, cardY + (above ? 80 : 0)); ctx.stroke();

            // Card
            ctx.fillStyle = ev.color + '10'; ctx.beginPath(); ctx.roundRect(x - 48, cardY, 96, 78, 8); ctx.fill();
            ctx.strokeStyle = ev.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x - 48, cardY, 96, 78, 8); ctx.stroke();

            ctx.fillStyle = ev.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ev.year, x, cardY + 16);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif';
            ctx.fillText(ev.name, x, cardY + 32);
            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(ev.energy, x, cardY + 48);
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(ev.desc, x, cardY + 64);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Each generation increased collision energy by orders of magnitude', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.6 – LHC Layout Overview */
export const LHCLayout = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 360;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Large Hadron Collider  -  Layout', W / 2, 24);

        const cx = 230, cy = 190, r = 120;

        // Main ring
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 6;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();

        // Beam direction arrows (two counter-rotating)
        const arrowAngles = [0, Math.PI / 2, Math.PI, 3 * Math.PI / 2];
        arrowAngles.forEach(a => {
            const ax2 = cx + (r + 2) * Math.cos(a);
            const ay2 = cy + (r + 2) * Math.sin(a);
            ctx.save(); ctx.translate(ax2, ay2); ctx.rotate(a + Math.PI / 2);
            ctx.fillStyle = '#2563EB';
            ctx.beginPath(); ctx.moveTo(0, -6); ctx.lineTo(5, 2); ctx.lineTo(-5, 2); ctx.closePath(); ctx.fill();
            ctx.restore();
        });

        // Experiments
        const experiments = [
            { name: 'ATLAS', angle: -Math.PI / 2, color: '#EF4444', desc: 'General purpose' },
            { name: 'CMS', angle: Math.PI / 2, color: '#3B82F6', desc: 'General purpose' },
            { name: 'ALICE', angle: Math.PI, color: '#22C55E', desc: 'Heavy ions' },
            { name: 'LHCb', angle: 0, color: '#F59E0B', desc: 'B-physics' },
        ];
        experiments.forEach(exp => {
            const ex = cx + r * Math.cos(exp.angle);
            const ey = cy + r * Math.sin(exp.angle);

            // Experiment dot
            ctx.beginPath(); ctx.arc(ex, ey, 12, 0, Math.PI * 2);
            ctx.fillStyle = exp.color; ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 8px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(exp.name.charAt(0), ex, ey);

            // Label
            const lx = ex + 20 * Math.cos(exp.angle);
            const ly = ey + 20 * Math.sin(exp.angle);
            ctx.fillStyle = exp.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textBaseline = 'alphabetic';
            ctx.textAlign = exp.angle > Math.PI / 2 || exp.angle < -Math.PI / 2 ? 'right' : 'left';
            if (Math.abs(exp.angle) === Math.PI / 2) ctx.textAlign = 'center';
            ctx.fillText(exp.name, lx, ly + (exp.angle < 0 ? -8 : 18));
            ctx.fillStyle = '#64748B'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(exp.desc, lx, ly + (exp.angle < 0 ? 4 : 30));
        });

        // Injection chain (right side)
        const ix = 400, iy = 60;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Injection Chain:', ix, iy);

        const chain = ['LINAC 4', 'PSB', 'PS', 'SPS', 'LHC'];
        chain.forEach((step, i) => {
            const y = iy + 18 + i * 28;
            ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(ix, y, 130, 22, 4); ctx.fill();
            ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(ix, y, 130, 22, 4); ctx.stroke();
            ctx.fillStyle = '#1E293B'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(step, ix + 65, y + 16);
            if (i < chain.length - 1) {
                ctx.fillStyle = '#94A3B8'; ctx.font = '12px Inter,sans-serif';
                ctx.fillText('↓', ix + 65, y + 27);
            }
        });

        // Stats
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Circumference: 26.7 km', ix, 240);
        ctx.fillText('Depth: ~100 m underground', ix, 258);
        ctx.fillText('Energy: 6.5 TeV per beam', ix, 276);
        ctx.fillText('Collisions: 13 TeV total', ix, 294);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Located at CERN, straddling the Franco-Swiss border near Geneva', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={360} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.7 – LHC Timeline (case study) */
export const LHCTimeline = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 240;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('LHC  -  Key Milestones', W / 2, 24);

        const events = [
            { year: '1998', label: 'Construction\nbegins', color: '#3B82F6' },
            { year: '2008', label: 'First beam +\nincident', color: '#EF4444' },
            { year: '2009', label: 'Restart at\n3.5 TeV', color: '#F59E0B' },
            { year: '2012', label: 'Higgs boson\ndiscovered!', color: '#22C55E' },
            { year: '2015', label: 'Run 2 at\n13 TeV', color: '#8B5CF6' },
        ];

        const axY = 110, axL = 50, axR = 510;
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(axL, axY); ctx.lineTo(axR, axY); ctx.stroke();

        const gap = (axR - axL) / (events.length - 1);
        events.forEach((ev, i) => {
            const x = axL + i * gap;

            ctx.beginPath(); ctx.arc(x, axY, 8, 0, Math.PI * 2);
            ctx.fillStyle = ev.color; ctx.fill();

            ctx.fillStyle = ev.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(ev.year, x, axY - 18);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            const lines = ev.label.split('\n');
            lines.forEach((line, li) => {
                ctx.fillText(line, x, axY + 28 + li * 14);
            });
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The LHC is the crowning achievement of decades of accelerator development', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={240} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 5.11 – Accelerator Comparison Table */
export const AcceleratorComparison = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Accelerator Types Compared', W / 2, 24);

        const types = [
            {
                name: 'Linac',
                maxE: '50 GeV',
                pros: 'No synchrotron\nradiation loss',
                cons: 'Very long\n(km-scale)',
                example: 'SLAC (3.2 km)',
                color: '#3B82F6',
            },
            {
                name: 'Cyclotron',
                maxE: '~1 GeV',
                pros: 'Compact,\ncontinuous beam',
                cons: 'Limited by\nrelativistic mass',
                example: 'Medical isotopes',
                color: '#22C55E',
            },
            {
                name: 'Synchrotron',
                maxE: '13 TeV',
                pros: 'Highest energy\nachievable',
                cons: 'Synch. radiation\n(for electrons)',
                example: 'LHC, SPS',
                color: '#8B5CF6',
            },
        ];

        const colW = 140, startX = 18, startY = 44;
        const rowLabels = ['Type', 'Max Energy', 'Advantages', 'Disadvantages', 'Example'];
        const rowH = [26, 28, 40, 40, 28];

        // Row labels
        let y = startY;
        rowLabels.forEach((label, ri) => {
            const h = rowH[ri];
            ctx.fillStyle = ri === 0 ? '#1E293B' : '#F8FAFC';
            if (ri > 0 && ri % 2 === 0) ctx.fillStyle = '#F1F5F9';
            ctx.fillRect(startX, y, 110, h);
            ctx.fillStyle = '#475569'; ctx.font = ri === 0 ? 'bold 11px Inter,sans-serif' : '11px Inter,sans-serif';
            if (ri === 0) ctx.fillStyle = 'white';
            ctx.textAlign = 'left';
            ctx.fillText(label, startX + 8, y + h / 2 + 4);
            y += h;
        });

        // Data columns
        types.forEach((type, ci) => {
            const x = startX + 112 + ci * (colW - 10);
            let y = startY;

            // Header
            ctx.fillStyle = type.color; ctx.beginPath(); ctx.roundRect(x, y, colW - 14, rowH[0], ci === 0 ? [8, 0, 0, 0] : ci === 2 ? [0, 8, 0, 0] : 0); ctx.fill();
            ctx.fillStyle = 'white'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(type.name, x + (colW - 14) / 2, y + 18);
            y += rowH[0];

            // Data
            const data = [type.maxE, type.pros, type.cons, type.example];
            data.forEach((val, di) => {
                const h = rowH[di + 1];
                ctx.fillStyle = (di + 1) % 2 === 0 ? '#F1F5F9' : '#F8FAFC';
                ctx.fillRect(x, y, colW - 14, h);

                ctx.fillStyle = '#1E293B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
                const lines = val.split('\n');
                lines.forEach((line, li) => {
                    const ly = y + (h / 2) - ((lines.length - 1) * 6) + li * 13 + 3;
                    ctx.fillText(line, x + (colW - 14) / 2, ly);
                });

                y += h;
            });
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Each type has trade-offs  -  synchrotrons dominate high-energy physics', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-indigo-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
